import axios from "axios";
import DecimalType from "decimal.js";
import { Logger } from "@nestjs/common";
import dayjs from "dayjs";

import { CURRENCY_DECIMALS } from "@/decimal-util";

import { RechargeResourceEndpoint } from "../../RechargeEndpoint";
import {
  IPageableRechargeDiscountResponse,
  IRechargeDiscount,
  IRechargeDiscountCreate,
  IRechargeDiscountGetParams,
  ISingleRechargeDiscountResponse,
} from "../../model/RechargeDiscount";
import { RechargeAccessTokenManager } from "../RechargeAccessTokenManager";

export class RechargeDiscountsClient extends RechargeResourceEndpoint<
ISingleRechargeDiscountResponse,
IPageableRechargeDiscountResponse,
IRechargeDiscountGetParams,
IRechargeDiscount
> {

  public constructor(
    rechargeKeyManager: RechargeAccessTokenManager,
    baseUrl = "https://api.rechargeapps.com",
  ) {
    super(
      axios.create({ baseURL: baseUrl }),
      rechargeKeyManager.getNextAccessToken.bind(rechargeKeyManager),
      "/discounts",
      new Logger(RechargeDiscountsClient.name),
    );
  }

  protected override singleGetter(t: ISingleRechargeDiscountResponse): IRechargeDiscount {
    return t.discount;
  }

  protected override multiGetter(t: IPageableRechargeDiscountResponse): IRechargeDiscount[] {
    return t.discounts;
  }

  /** Create a one-use fixed_amount discount code in ReCharge
   * @param code The discount code string.
   * Must be unique per discount.
   * @param discountChargeId The ID of the row in the discount_charge table that contains the given discount code.
   * The value is stored in the external_discount_id field of the ReCharge discount
   * to make debugging and analytics easier.
   * @param amount A fixed monetary discount amount (e.g. $15.45).
   * @return the created discount
   */
  public async createDiscount(
    code: string,
    discountChargeId: number,
    amount: DecimalType,
  ): Promise<IRechargeDiscount> {
    const data: IRechargeDiscountCreate = {
      code,
      external_discount_id: { ecommerce: discountChargeId.toString() },
      external_discount_source: "headless",
      usage_limits: {
        one_application_per_customer: false,
        first_time_customer_restriction: false,
        max_subsequent_redemptions: 0,
        redemptions: 1,
      },
      value: amount.toFixed(CURRENCY_DECIMALS, DecimalType.ROUND_HALF_UP),
      value_type: "fixed_amount",
      ends_at: dayjs().add(28, "days").format(),
    };
    const response = await this.requestWithRetry<ISingleRechargeDiscountResponse, IRechargeDiscountCreate>(
      {
        url: this.resourcePath,
        config: {
          headers: this.getHeaders(),
          method: "POST",
          data,
        },
        newKeyOnRetry: true,
        // It's good practice to not retry creation requests, because it can result into duplicates.
        // However, in this case, it's fine, since ReCharge enforces the uniqueness of discount codes.
        // In the worst case, the request will just fail with an error saying:
        // {
        //   "errors": {
        //     "code": [
        //       "Code already exists: 195329530"
        //     ]
        //   }
        // }
        // That should be acceptable since we can just retry later.
        // If it happens often enough, we can add some extra handling for that.
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data.discount;
  }

}
